import React, { useEffect } from 'react';
import { getSettingsClass } from '~/utils/helpers';
import transcodingConfig from '../../../data/config/transcoding-config.json';
import { graphql } from 'gatsby';
import PartialCTA from '../partials/cta';

interface TranscodingCalculatorProps {
  block: {
    settings: Settings;
    total_cost_limit: number;
    transcoding_packages: {
      title?: string;
      subtitle?: string;
      resolution_label: string;
    };
    output_codecs: {
      title?: string;
      subtitle?: string;
    };
    video_duration: {
      title?: string;
      subtitle?: string;
      min_duration: number;
      max_duration: number;
    };
  };
}

type PackageType = (typeof transcodingConfig.transcoding_packages)[number];

const TranscodingCalculator: React.FC<TranscodingCalculatorProps> = ({
  block,
}) => {
  const settingsClass = getSettingsClass(block.settings);
  const [selectedPackage, setSelectedPackage] = React.useState(
    transcodingConfig.transcoding_packages[0]!
  );
  const [codecs, setCodecs] = React.useState<string[]>([
    selectedPackage.output_codecs[0]!,
  ]);
  const [videoDuration, setVideoDuration] = React.useState<number>(
    block.video_duration.min_duration
  );
  const [supportedPackages, setSupportedPackages] = React.useState<string[]>([
    ...selectedPackage.resolutions,
  ]);
  const [basePrice, setBasePrice] = React.useState<number>(
    selectedPackage.price
  );

  const [aboveLimit, setAboveLimit] = React.useState(false);

  const costPerMinute = basePrice * codecs.length;
  const totalCost = costPerMinute * videoDuration;

  const handlePackageChange = (item: PackageType) => () => {
    setSelectedPackage(item);
    setBasePrice(item.price);
    setSupportedPackages(item.resolutions);
  };

  const handleVideoDurationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const videoDuration = Number.parseFloat(event.target.value) || 0;
    if (videoDuration < block.video_duration.min_duration) {
      setVideoDuration(block.video_duration.min_duration);
    } else if (videoDuration > block.video_duration.max_duration) {
      setVideoDuration(block.video_duration.max_duration);
    } else {
      setVideoDuration(videoDuration);
    }
  };

  const handleCodecSelection = (codec: string) => () => {
    if (codecs.includes(codec)) {
      if (codecs.length > 1) {
        setCodecs(codecs.filter(item => item !== codec));
      }
    } else {
      setCodecs([...codecs, codec]);
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    });
  };

  useEffect(() => {
    setAboveLimit(totalCost > block.total_cost_limit);
  }, [totalCost]);

  return (
    <div
      className={`block block__transcoding-calculator my-0 mx-auto flex flex-col bg-[#fff] rounded-xl md:rounded-none w-[80rem] md:w-[100%] lg:w-[70rem] ${settingsClass}`}
    >
      <div className='flex flex-col gap-8 border-b border-solid border-gray-lighter p-12 lg:p-8'>
        <div className='flex gap-4'>
          {block.transcoding_packages.title && (
            <div className='leading-12 text-[3.2rem] font-semibold not-italic'>
              {block.transcoding_packages.title}
            </div>
          )}
          {block.transcoding_packages.subtitle && (
            <div className='text-[1.6rem] leading-8 text-menu-title text-shadow-light'>
              {block.transcoding_packages.subtitle}
            </div>
          )}
        </div>
        <div className='scroll flex flex-row gap-4 py-0 md:flex-col sm:py-4'>
          {transcodingConfig.transcoding_packages.map(item => {
            const packageClass =
              selectedPackage?.type === item.type
                ? 'border-orange-gradient after:absolute after:top-[1.2rem] after:right-[1.2rem] after:size-[1.6rem] after:flex after:bg-tick-ok'
                : 'border-gray-gradient';
            return (
              <button
                key={item.type}
                type='button'
                className={`relative flex flex-col gap-2 py-[1.2rem] px-[1.6rem] flex-1 border-2 border-rounded-soft border-gradient box-shadow-light hover:border-orange-gradient ${packageClass}`}
                onClick={handlePackageChange(item)}
                aria-label={`Select ${item.type} package`}
              >
                <img
                  className='block h-[10.7rem] w-[13.5rem] self-center lg:hidden'
                  src={item.image.src}
                  alt={item.image.alt}
                />
                <div className='w-full flex flex-row lg:flex-col items-center lg:items-start justify-between'>
                  <div className='text-text-primary text-[2rem] lg:text-[4.5rem] font-bold leading-none lg:leading-tight'>
                    {item.type}
                  </div>
                  <div
                    className={`text-dark-blue text-[1.4rem] leading-none text-shadow-light before:content-['$'] after:content-['/min'] lg:text-[2.8rem] lg:leading-normal`}
                  >
                    {item.price}
                  </div>
                </div>
                <p className='text-[1.2rem] lg:text-[1.7rem] text-shadow-light text-left text-text-gray leading-9 lg:leading-normal'>
                  {item.text}
                </p>
              </button>
            );
          })}
        </div>
        <div className='flex flex-col gap-4'>
          <p
            className='m-0 mt-4 text-[1.3rem] font-light leading-9 text-menu-title text-shadow-light'
            dangerouslySetInnerHTML={{
              __html: selectedPackage.resolutions_label,
            }}
          />

          <ul className='flex flex-wrap items-center self-stretch overflow-hidden border-rounded-soft'>
            {transcodingConfig.transcoding_resolutions.map(resolution => {
              const resolutionClass = supportedPackages.includes(resolution)
                ? 'text-menu-title before:size-[1.6rem] before:flex before:bg-tick-ok'
                : 'text-text-gray ';
              return (
                <li
                  key={resolution}
                  className={`flex flex-1 items-center justify-center gap-1 border border-solid border-gray-lighter px-[1.6rem] py-[1.2rem] duration-300 ease-in-out ${resolutionClass}`}
                >
                  {resolution}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <div className='flex flex-col gap-8 border-b border-solid border-gray-lighter p-12 lg:p-8'>
        <div className='output-codecs__info'>
          {block.output_codecs.title && (
            <div className='leading-12 text-[3.2rem] font-semibold not-italic'>
              {block.output_codecs.title}
            </div>
          )}
          {block.output_codecs.subtitle && (
            <div className='text-[1.6rem] leading-8 text-menu-title text-shadow-light'>
              {block.output_codecs.subtitle}
            </div>
          )}
        </div>
        <div className='flex flex-row flex-wrap gap-4 lg:flex-col'>
          {transcodingConfig.output_codecs.map(codec => {
            const codecClass = codecs.includes(codec)
              ? 'border-orange-gradient relative after:absolute after:top-[1.2rem] after:right-[1.2rem] after:size-[1.6rem] after:flex after:bg-tick-ok'
              : 'border-gray-gradient';
            return (
              <div
                key={codec}
                role='button'
                className={`flex flex-col px-[1.2rem] py-[1.6rem] justify-center items-center gap-1 border-2 border-rounded-soft box-shadow-light flex-1 border-gray-lighter border-gradient hover:border-orange-gradient ${codecClass}`}
                onClick={handleCodecSelection(codec)}
              >
                <div className='text-[2rem] font-bold leading-8 text-text-primary'>
                  {codec}
                </div>
                <div className='text-text-gray leading-8 text-[1.4rem] text-shadow-light'>{`($${selectedPackage?.price || 0}/min)`}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className='flex flex-col gap-8 border-b border-solid border-gray-lighter p-12 lg:p-8'>
        <div className='flex flex-row items-center justify-between gap-4 lg:flex-col lg:items-stretch sm:gap-12'>
          <div className='flex-1'>
            {block.video_duration.title && (
              <div className='leading-12 text-[3.2rem] font-semibold not-italic'>
                {block.video_duration.title}
              </div>
            )}
            {block.video_duration.subtitle && (
              <div className='text-[1.6rem] leading-8 text-menu-title text-shadow-light'>
                {block.video_duration.subtitle}
              </div>
            )}
          </div>
          <div className='relative flex flex-1 py-6 px-6 justify-end items-end lg:items-center max-w-[20ch] lg:max-w-full border border-solid border-gray-lighter border-rounded-soft'>
            <input
              className='input-native-reset bg-transparent absolute top-0 right-0 bottom-0 w-full h-full text-[3.5rem] text-menu-primary text-shadow-light text-right pr-[8rem] font-semibold border-rounded-soft'
              type='number'
              value={videoDuration}
              onChange={handleVideoDurationChange}
              inputMode='numeric'
              onWheel={e => e.currentTarget.blur()}
            />
            <span className='select-none touch-none text-menu-primary text-shadow-light text-[3.5rem] font-semibold'>
              min
            </span>
          </div>
        </div>
        <div className='slidecontainer block lg:hidden'>
          <input
            type='range'
            className='slider'
            min={block.video_duration.min_duration}
            max={block.video_duration.max_duration}
            value={videoDuration}
            onChange={handleVideoDurationChange}
          />
        </div>
      </div>
      <div className='flex content-stretch items-center border-b border-solid border-gray-lighter p-12 lg:p-8'>
        <div className='flex-1 text-[2.1rem] text-menu-title text-shadow-light'>
          Cost per minute
        </div>
        <div
          className={`after:content-['/ per minute'] mr-2 text-[3.5rem] font-semibold text-menu-primary text-shadow-light`}
        >
          ${formatNumber(costPerMinute)}
        </div>
        {/* <div className={`text-[1.3rem] text-menu-title text-shadow-light`}>
          / per minute
        </div> */}
      </div>
      <div className='flex content-stretch items-center gap-2 p-12 lg:p-8'>
        <div className='text-[2.4rem] font-bold md:leading-normal leading-[3.52] flex-1 text-shadow-light'>
          Total cost
        </div>
        {aboveLimit ? (
          <PartialCTA
            data={{
              cta_text: 'Contact Sales',
              cta_url: '/contact-sales',
              cta_type: 'compact',
              wr_class: 'flex items-center',
            }}
          />
        ) : (
          <div className='md:text-[4rem] text-[4.5rem] text-orange-gradient font-bold text-shadow-light flex-1 text-right'>{`$${formatNumber(totalCost)}`}</div>
        )}
      </div>
    </div>
  );
};

export const transcodingCalculatorFragment = graphql`
  fragment TranscodingCalculatorFragment on Block {
    _template
    settings {
      ...SettingsFragment
    }
    total_cost_limit
    transcoding_packages {
      title
      subtitle
      resolution_label
    }
    output_codecs {
      title
      subtitle
    }
    video_duration {
      title
      subtitle
      min_duration
      max_duration
    }
  }
`;

export default TranscodingCalculator;
